import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { FaCalendarCheck, FaChartBar, FaCheckCircle, FaDatabase, FaFileInvoiceDollar, FaRobot } from "react-icons/fa";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RepeatIcon from "@mui/icons-material/Repeat";
import BarChartIcon from "@mui/icons-material/BarChart";
import LinkIcon from "@mui/icons-material/Link";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { useLanguage } from "./context/LanguageContext";
import { homeContent } from "./content/siteCopy";
import { getLanguagePrefix } from "./routing/localizedPaths";
import { getLocalizedService, getServiceIds, getServicePath } from "./services/catalog";
import { serviceIcons } from "./services/serviceIcons";
import theme from "./theme";

const heroCopy = {
  no: {
    eyebrow: "AI, data og software for norske SMB-er",
    title: "Mindre manuelt arbeid. Full kontroll på driften.",
    subtitle:
      "Vi bygger lønnsomme forbedringer som faktisk blir brukt: AI-arbeidsflyter, Power BI-dashbord, Microsoft Fabric-datalag, bookingflyt og interne verktøy rundt økonomi og drift.",
    primaryCta: "Book en prat",
    secondaryCta: "Se tjenester",
  },
  en: {
    eyebrow: "AI, data, and software for Norwegian SMBs",
    title: "Less manual work. Full control of operations.",
    subtitle:
      "VImplement builds profitable improvements that actually get used: AI workflows, Power BI management data, Microsoft Fabric data layers, booking flows, and internal tools around finance and operations.",
    primaryCta: "Book a call",
    secondaryCta: "See services",
  },
};

// Hero product mock — static illustrative numbers (the caption labels it an
// example) so the first screen *shows* the operating picture we deliver
// instead of a stock photo.
const heroMock = {
  no: {
    url: "app.vimplement.com/styringsdata",
    kpis: [
      { label: "Omsetning MTD", value: "1 248 400 kr", delta: "+12 %" },
      { label: "Utestående", value: "184 200 kr", delta: "−8 %" },
      { label: "Bookinger", value: "23", delta: "+5" },
    ],
    chartLabel: "Omsetning per uke",
    chartRange: "Siste 12 uker",
    activity: [
      { text: "EHF-faktura sendt og godkjent · bilag 2041", color: "#0E7C7B" },
      { text: "AI-triage: 3 henvendelser rutet til riktig eier", color: "#0E7C7B" },
      { text: "Purring planlagt · forfall om 14 dager", color: "#C47A2C" },
    ],
    caption: "Illustrasjon: et typisk styringsbilde slik vi setter det opp.",
  },
  en: {
    url: "app.vimplement.com/styringsdata",
    kpis: [
      { label: "Revenue MTD", value: "1 248 400 kr", delta: "+12 %" },
      { label: "Outstanding", value: "184 200 kr", delta: "−8 %" },
      { label: "Bookings", value: "23", delta: "+5" },
    ],
    chartLabel: "Revenue per week",
    chartRange: "Last 12 weeks",
    activity: [
      { text: "EHF invoice sent and approved · voucher 2041", color: "#0E7C7B" },
      { text: "AI triage: 3 inquiries routed to the right owner", color: "#0E7C7B" },
      { text: "Reminder scheduled · due in 14 days", color: "#C47A2C" },
    ],
    caption: "Illustration: a typical operations view as we set it up.",
  },
};

const stats = {
  no: [
    { value: 30, suffix: " %", label: "Norske foretak (10+ ansatte) brukte AI i 2025", source: "SSB 2025", icon: <FaRobot /> },
    { value: 77, suffix: " %", label: "av etternølerne peker på manglende AI-kompetanse som hinder", source: "SSB 2025", icon: <FaCheckCircle /> },
    { value: 2027, suffix: "", label: "Foreslått oppstart for plikt til å sende e-faktura B2B", source: "Finansdepartementet 2025", icon: <FaDatabase /> },
  ],
  en: [
    { value: 30, suffix: " %", label: "Enterprises with at least 10 employees using AI in 2025", source: "SSB 2025", icon: <FaRobot /> },
    { value: 77, suffix: " %", label: "Late adopters citing lack of AI competence as a barrier", source: "SSB 2025", icon: <FaCheckCircle /> },
    { value: 2027, suffix: "", label: "Proposed start for mandatory B2B e-invoicing", source: "Ministry of Finance 2025", icon: <FaDatabase /> },
  ],
};

const productItems = {
  no: [
    {
      id: "styringsdata",
      title: "Styringsdata",
      badge: "Power BI",
      description: "Ferdige dashbord for økonomi, salg og drift – koblet til kildene norske SMB-er faktisk bruker.",
      demoUrl: "https://styringsdata.pages.dev/demo/okonomi",
      icon: <FaChartBar />,
    },
    {
      id: "bookingflyt",
      title: "Bookingflyt",
      badge: "Booking",
      description: "Egen bookingside med tjenestevalg, ledige tider, bekreftelse og admin-oversikt.",
      demoUrl: "https://bookingflyt.pages.dev/book/demo",
      icon: <FaCalendarCheck />,
    },
    {
      id: "fakturaflyt",
      title: "Fakturaflyt",
      badge: "EHF",
      description: "EHF-sending, SAF-T-eksport og kontrollflyt som gir bedre oversikt på fakturering og dokumentasjon.",
      demoUrl: "https://fakturaflyt.pages.dev/demo",
      icon: <FaFileInvoiceDollar />,
    },
  ],
  en: [
    {
      id: "styringsdata",
      title: "Styringsdata",
      badge: "Power BI",
      description: "Pre-built dashboards for finance, sales, and operations connected to the sources SMBs actually use.",
      demoUrl: "https://styringsdata.pages.dev/demo/okonomi",
      icon: <FaChartBar />,
    },
    {
      id: "bookingflyt",
      title: "Bookingflyt",
      badge: "Booking",
      description: "A dedicated booking surface with service selection, available slots, confirmation, and admin overview.",
      demoUrl: "https://bookingflyt.pages.dev/book/demo",
      icon: <FaCalendarCheck />,
    },
    {
      id: "fakturaflyt",
      title: "Fakturaflyt",
      badge: "EHF",
      description: "EHF, SAF-T, and control flow that makes invoicing and documentation more manageable.",
      demoUrl: "https://fakturaflyt.pages.dev/demo",
      icon: <FaFileInvoiceDollar />,
    },
  ],
};

const fade = (delay = 0) => ({ initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { delay, duration: 0.5 } });

const Home = () => {
  const { language } = useLanguage();
  const prefix = getLanguagePrefix(language);
  const copy = homeContent[language] ?? homeContent.en;
  const hero = heroCopy[language] ?? heroCopy.en;
  const statItems = stats[language] ?? stats.en;
  const products = productItems[language] ?? productItems.en;
  const productsPath = language === "no" ? `${prefix}/produkter` : `${prefix}/products`;
  const mock = heroMock[language] ?? heroMock.en;

  const services = useMemo(
    () => getServiceIds().map((serviceId) => ({ ...getLocalizedService(serviceId, language), icon: serviceIcons[serviceId] })),
    [language]
  );

  return (
    <Box sx={{ backgroundColor: "white" }}>
      {/* ── Hero ── */}
      <Box
        sx={{
          // Tighter top/bottom on mobile so the primary CTA is reachable
          // closer to the fold on a 375x667 viewport.
          pt: { xs: 6, md: 14 },
          pb: { xs: 8, md: 14 },
          background:
            "radial-gradient(1100px 540px at 82% -12%, rgba(14,124,123,0.30), transparent 62%), linear-gradient(180deg, #0B1220 0%, #0F172A 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, border: "1px solid rgba(255,255,255,0.16)", borderRadius: 999, pl: 1, pr: 1.75, py: 0.6, backgroundColor: "rgba(255,255,255,0.06)", mb: 3 }}>
                  <Box aria-hidden="true" sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#0E7C7B", boxShadow: "0 0 0 3px rgba(14,124,123,0.18)" }} />
                  <Typography component="span" sx={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7BD8D5" }}>
                    {hero.eyebrow}
                  </Typography>
                </Box>
                <Typography component="h1" sx={{ fontSize: { xs: "2.1rem", md: "3.65rem" }, fontWeight: 700, lineHeight: { xs: 1.1, md: 1.05 }, letterSpacing: "-0.025em", color: "#FFFFFF", mb: { xs: 2.5, md: 3 }, maxWidth: 760, overflowWrap: "anywhere" }}>
                  {hero.title}
                </Typography>
                <Typography sx={{ fontSize: { xs: "1rem", md: "1.15rem" }, lineHeight: 1.65, color: "#CBD5E1", maxWidth: 620, mb: { xs: 3.5, md: 5 } }}>
                  {hero.subtitle}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: { xs: 1.5, md: 2 }, mb: 1.5 }}>
                  <Button component={Link} to={`${prefix}/book`} sx={{ backgroundColor: "#FFFFFF", color: "#0F172A", fontWeight: 700, borderRadius: "10px", px: { xs: 4, md: 5 }, py: { xs: 1.6, md: 1.85 }, minHeight: 48, fontSize: { xs: "1rem", md: "1.05rem" }, textTransform: "none", boxShadow: "0 14px 30px -12px rgba(0,0,0,0.55)", width: { xs: "100%", sm: "auto" }, "&:hover": { backgroundColor: "#E6EDF5", boxShadow: "0 16px 34px -12px rgba(0,0,0,0.6)" } }}>
                    {hero.primaryCta}
                  </Button>
                  <Button component={Link} to={`${prefix}/services`} sx={{ color: "#E2E8F0", fontWeight: 600, borderRadius: "10px", px: 2.5, py: 1.4, minHeight: 44, textTransform: "none", backgroundColor: "transparent", width: { xs: "100%", sm: "auto" }, "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" } }}>
                    {hero.secondaryCta} →
                  </Button>
                </Box>
                <Typography sx={{ fontSize: "0.85rem", color: "#94A3B8", fontWeight: 500, mb: 5 }}>
                  {language === "no" ? "Svar innen én virkedag · Ingen forpliktelser" : "Reply within one business day · No commitment"}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: { xs: 1.5, md: 0 } }}>
                  {copy.trustStrip.map((item, i) => (
                    <React.Fragment key={item}>
                      {i > 0 && <Box aria-hidden="true" sx={{ display: { xs: "none", md: "block" }, width: 4, height: 4, borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.28)", mx: 1.5 }} />}
                      <Typography sx={{ fontSize: "0.85rem", color: "#94A3B8", fontWeight: 500 }}>{item}</Typography>
                    </React.Fragment>
                  ))}
                </Box>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={5}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.6 }}>
                <Box sx={{ borderRadius: "16px", border: "1px solid", borderColor: "divider", boxShadow: theme.gradients.scale.shadow.raised, overflow: "hidden", backgroundColor: "white" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25, borderBottom: "1px solid #E2E8F0", backgroundColor: "#F8FAFC" }}>
                    <Box aria-hidden="true" sx={{ display: "flex", gap: 0.6 }}>
                      {["#FCA5A5", "#FCD34D", "#86EFAC"].map((dot) => (
                        <Box key={dot} sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: dot }} />
                      ))}
                    </Box>
                    <Box sx={{ flexGrow: 1, mx: 1, px: 1.5, py: 0.4, borderRadius: "6px", border: "1px solid #E2E8F0", backgroundColor: "white", fontSize: "0.7rem", color: "#64748B", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {mock.url}
                    </Box>
                  </Box>
                  <Box sx={{ p: 2.5, backgroundColor: "#F8FAFC" }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5, mb: 1.5 }}>
                      {mock.kpis.map((kpi) => (
                        <Box key={kpi.label} sx={{ p: 1.5, borderRadius: "10px", border: "1px solid #E2E8F0", backgroundColor: "white", minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.62rem", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{kpi.label}</Typography>
                          <Typography sx={{ fontSize: { xs: "0.85rem", md: "0.95rem" }, fontWeight: 700, color: "#0F172A", lineHeight: 1.2, whiteSpace: "nowrap" }}>{kpi.value}</Typography>
                          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#0E7C7B" }}>{kpi.delta}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ p: 2, borderRadius: "10px", border: "1px solid #E2E8F0", backgroundColor: "white", mb: 1.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1.5 }}>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#0F172A" }}>{mock.chartLabel}</Typography>
                        <Typography sx={{ fontSize: "0.62rem", color: "#94A3B8", fontWeight: 600 }}>{mock.chartRange}</Typography>
                      </Box>
                      <Box aria-hidden="true" sx={{ display: "flex", alignItems: "flex-end", gap: 0.75, height: 72 }}>
                        {[34, 48, 42, 58, 50, 66, 57, 72, 64, 78, 84, 96].map((bar, index, bars) => (
                          <Box key={index} sx={{ flexGrow: 1, height: `${bar}%`, borderRadius: "3px 3px 0 0", backgroundColor: index === bars.length - 1 ? "#0E7C7B" : "#CBD5E1" }} />
                        ))}
                      </Box>
                    </Box>
                    <Box sx={{ display: "grid", gap: 1 }}>
                      {mock.activity.map((row) => (
                        <Box key={row.text} sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.75, py: 1.1, borderRadius: "10px", border: "1px solid #E2E8F0", backgroundColor: "white" }}>
                          <Box aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: row.color, flexShrink: 0 }} />
                          <Typography sx={{ fontSize: "0.78rem", color: "#334155", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.text}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
                <Typography sx={{ mt: 1.5, fontSize: "0.75rem", color: "#94A3B8", textAlign: "center" }}>{mock.caption}</Typography>
              </motion.div>
            </Grid>
          </Grid>

          <Box sx={{ mt: { xs: 7, md: 10 }, pt: 4, borderTop: "1px solid rgba(255,255,255,0.12)", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", columnGap: { xs: 3, md: 5 }, rowGap: 1.5 }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94A3B8", width: { xs: "100%", md: "auto" }, textAlign: "center" }}>
              {language === "no" ? "Bygger på" : "Built on"}
            </Typography>
            {["Microsoft Fabric", "Power BI", "Azure", "OpenAI", "Tripletex", "Fiken"].map((tech) => (
              <Typography key={tech} sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#CBD5E1", letterSpacing: "-0.01em" }}>{tech}</Typography>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Stats ── */}
      <Box component="section" aria-label={language === "no" ? "Nøkkeltall" : "Key statistics"} sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#FFFFFF", borderBottom: "1px solid #EDF2F7", position: "relative", overflow: "hidden" }}>
        <Container maxWidth="lg" sx={{ position: "relative" }}>
          <Grid container spacing={0}>
            {statItems.map((stat, index) => (
              <Grid item xs={12} md={4} key={stat.label} sx={{ borderLeft: { md: index === 0 ? "none" : "1px solid #EDF2F7" }, borderTop: { xs: index === 0 ? "none" : "1px solid #EDF2F7", md: "none" }, py: { xs: 4, md: 1 }, px: { md: 4 } }}>
                <motion.div {...fade(index * 0.08)}>
                  <Box sx={{ textAlign: "center" }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: "10px", mx: "auto", mb: 2, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(14,124,123,0.10)", color: "#0E7C7B", fontSize: "0.95rem" }}>
                      {stat.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: "2.4rem", md: "3.25rem" }, color: "#0F172A", letterSpacing: "-0.02em", lineHeight: 1, mb: 1.5 }}>
                      {stat.value >= 2000 ? `${stat.value}` : <CountUp end={stat.value} duration={2.2} suffix={stat.suffix} />}
                    </Typography>
                    <Box aria-hidden="true" sx={{ width: 28, height: 2, borderRadius: 1, backgroundColor: "#0E7C7B", opacity: 0.85, mx: "auto", mb: 2 }} />
                    <Typography sx={{ color: "#475569", fontSize: "0.92rem", lineHeight: 1.55, maxWidth: 280, mx: "auto", mb: 1 }}>{stat.label}</Typography>
                    <Typography sx={{ color: "#94A3B8", fontSize: "0.72rem", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>{stat.source}</Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Challenges ── */}
      <Box sx={{ py: { xs: 12, md: 16 }, backgroundColor: "#F8FAFC" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0E7C7B", mb: 2 }}>
              {copy.challengesEyebrow}
            </Typography>
            <Typography component="h2" sx={{ fontSize: { xs: "1.85rem", md: "2.5rem" }, fontWeight: 700, letterSpacing: "-0.02em", mb: 2 }}>
              {copy.problemsTitle}
            </Typography>
            <Typography sx={{ maxWidth: 620, mx: "auto", lineHeight: 1.7, color: "#475569" }}>
              {copy.problemsSubtitle}
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {copy.problems.map((item, i) => {
              const iconMap = { repeat: <RepeatIcon sx={{ fontSize: 22 }} />, chart: <BarChartIcon sx={{ fontSize: 22 }} />, link: <LinkIcon sx={{ fontSize: 22 }} /> };
              return (
                <Grid item xs={12} md={4} key={item.title}>
                  <motion.div {...fade(i * 0.1)}>
                    <Card sx={{ height: "100%", border: "1px solid #E2E8F0", borderRadius: "14px", boxShadow: "none", transition: "all .25s ease", "&:hover": { borderColor: "#0E7C7B", transform: "translateY(-2px)", boxShadow: "0 12px 28px -16px rgba(15,23,42,0.18)" } }}>
                      <CardContent sx={{ p: 4 }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: "10px", mb: 3, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(14,124,123,0.08)", color: "#0E7C7B", border: "1px solid rgba(14,124,123,0.15)" }}>
                          {iconMap[item.icon]}
                        </Box>
                        <Typography component="h3" sx={{ fontWeight: 700, mb: 1.5, fontSize: "1.05rem", letterSpacing: 0 }}>{item.title}</Typography>
                        <Typography sx={{ lineHeight: 1.7, color: "#475569", fontSize: "0.9rem" }}>{item.description}</Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* ── Services ── */}
      <Box sx={{ py: { xs: 12, md: 16 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0E7C7B", mb: 2 }}>{copy.servicesEyebrow}</Typography>
            <Typography component="h2" sx={{ fontSize: { xs: "1.85rem", md: "2.5rem" }, fontWeight: 700, letterSpacing: "-0.02em", mb: 2 }}>{copy.servicesTitle}</Typography>
            <Typography sx={{ color: "#475569", maxWidth: 600, mx: "auto", lineHeight: 1.7 }}>{copy.servicesSubtitle}</Typography>
          </Box>
          <Grid container spacing={4}>
            {services.map((service, i) => {
              return (
                <Grid item xs={12} md={6} key={service.id}>
                  <motion.div {...fade(i * 0.08)}>
                    <Card sx={{ height: "100%", border: "1px solid #E2E8F0", borderTop: "3px solid #0E7C7B", borderRadius: "14px", boxShadow: "none", overflow: "hidden", transition: "all .25s ease", "&:hover": { borderColor: "#0E7C7B", transform: "translateY(-3px)", boxShadow: "0 18px 36px -20px rgba(15,23,42,0.22)" } }}>
                      <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(14,124,123,0.08)", color: "#0E7C7B", border: "1px solid rgba(14,124,123,0.15)" }}>
                            {service.icon}
                          </Box>
                          <Typography component="h3" sx={{ fontWeight: 700, fontSize: "1.1rem", letterSpacing: 0 }}>{service.title}</Typography>
                        </Box>
                        <Typography sx={{ color: "#475569", lineHeight: 1.7, fontSize: "0.92rem", mb: 3 }}>{service.teaser}</Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 3 }}>
                          {service.technologies.slice(0, 4).map((tech) => (
                            <Chip key={tech} label={tech} size="small" sx={{ height: 26, backgroundColor: "#F4F8FB", border: "1px solid #E2E8F0", color: "#334155", fontWeight: 600, fontSize: "0.74rem", letterSpacing: "0.01em" }} />
                          ))}
                        </Box>
                        <Button component={Link} to={getServicePath(service.id, language)} endIcon={<Box component="span" sx={{ transition: "transform .2s ease", display: "inline-block" }}>→</Box>} sx={{ px: 0, color: "#0F172A", fontWeight: 700, textTransform: "none", "&:hover": { backgroundColor: "transparent", color: "#0E7C7B", "& .MuiButton-endIcon span": { transform: "translateX(3px)" } } }}>
                          {copy.seeService}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* ── Products ── */}
      <Box sx={{ py: { xs: 12, md: 16 }, backgroundColor: "#F8FAFC" }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="flex-end" sx={{ mb: { xs: 5, md: 7 } }}>
            <Grid item xs={12} md={7}>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0E7C7B", mb: 2 }}>
                {copy.productsEyebrow}
              </Typography>
              <Typography component="h2" sx={{ fontSize: { xs: "1.85rem", md: "2.5rem" }, fontWeight: 700, letterSpacing: "-0.02em", mb: 2 }}>
                {copy.productsTitle}
              </Typography>
              <Typography sx={{ color: "#475569", maxWidth: 680, lineHeight: 1.7 }}>
                {copy.productsSubtitle}
              </Typography>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
              <Button component={Link} to={productsPath} sx={{ backgroundColor: "#0F172A", color: "white", fontWeight: 700, borderRadius: "10px", px: 3.5, py: 1.4, textTransform: "none", "&:hover": { backgroundColor: "#1E293B" } }}>
                {copy.productsPrimaryCta}
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {products.map((product, index) => (
              <Grid item xs={12} md={4} key={product.id}>
                <motion.div {...fade(index * 0.08)}>
                  <Card sx={{ height: "100%", border: "1px solid #E2E8F0", borderRadius: "14px", boxShadow: "none", transition: "all .25s ease", "&:hover": { borderColor: "#0E7C7B", transform: "translateY(-2px)", boxShadow: "0 14px 30px -20px rgba(15,23,42,0.22)" } }}>
                    <CardContent sx={{ p: 4, height: "100%", display: "flex", flexDirection: "column" }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 3 }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(14,124,123,0.08)", color: "#0E7C7B", border: "1px solid rgba(14,124,123,0.15)", fontSize: "1rem" }}>
                          {product.icon}
                        </Box>
                        <Chip label={product.badge} size="small" sx={{ height: 26, backgroundColor: "#F4F8FB", border: "1px solid #E2E8F0", color: "#334155", fontWeight: 700, fontSize: "0.72rem" }} />
                      </Box>
                      <Typography component="h3" sx={{ fontWeight: 700, fontSize: "1.08rem", letterSpacing: 0, mb: 1.5 }}>
                        {product.title}
                      </Typography>
                      <Typography sx={{ color: "#475569", lineHeight: 1.7, fontSize: "0.92rem", mb: 3 }}>
                        {product.description}
                      </Typography>
                      <Box sx={{ mt: "auto", display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        <Button component={Link} to={`${productsPath}#${product.id}`} sx={{ px: 0, color: "#0F172A", fontWeight: 700, textTransform: "none", "&:hover": { backgroundColor: "transparent", color: "#0E7C7B" } }}>
                          {copy.productsPrimaryCta}
                        </Button>
                        <Button component="a" href={product.demoUrl} target="_blank" rel="noopener noreferrer" endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />} sx={{ px: 0, color: "#0E7C7B", fontWeight: 700, textTransform: "none", "&:hover": { backgroundColor: "transparent", color: "#075E5C" } }}>
                          {copy.productsDemoCta}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Process ── */}
      <Box sx={{ py: { xs: 12, md: 16 }, backgroundColor: "#FFFFFF" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0E7C7B", mb: 2 }}>{copy.processEyebrow}</Typography>
            <Typography component="h2" sx={{ fontSize: { xs: "1.85rem", md: "2.5rem" }, fontWeight: 700, letterSpacing: "-0.02em", mb: 2 }}>{copy.processTitle}</Typography>
            <Typography sx={{ color: "#475569", maxWidth: 600, mx: "auto", lineHeight: 1.7 }}>{copy.processSubtitle}</Typography>
          </Box>
          <Box sx={{ position: "relative" }}>
            <Box aria-hidden="true" sx={{ display: { xs: "none", md: "block" }, position: "absolute", top: 24, left: "12.5%", right: "12.5%", height: 0, borderTop: "1px dashed #CBD5E1" }} />
            <Grid container spacing={4} sx={{ position: "relative" }}>
              {copy.process.map((item, i) => (
                <Grid item xs={12} md={3} key={item.step}>
                  <motion.div {...fade(i * 0.08)}>
                    <Box sx={{ textAlign: "center" }}>
                      <Box sx={{ width: 48, height: 48, borderRadius: "50%", mx: "auto", mb: 2.5, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#FFFFFF", color: "#0F172A", fontWeight: 700, fontSize: "0.92rem", letterSpacing: "0.02em", border: "1px solid #E2E8F0", boxShadow: "0 2px 8px -3px rgba(15,23,42,0.08)", position: "relative", zIndex: 1 }}>
                        {item.step}
                      </Box>
                      <Typography component="h3" sx={{ fontWeight: 700, mb: 1, fontSize: "1.02rem", letterSpacing: 0 }}>{item.title}</Typography>
                      <Typography sx={{ color: "#475569", lineHeight: 1.7, fontSize: "0.9rem", maxWidth: 240, mx: "auto" }}>{item.description}</Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* ── Case studies ── */}
      <Box sx={{ py: { xs: 12, md: 16 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0E7C7B", mb: 2 }}>{copy.casesEyebrow}</Typography>
            <Typography component="h2" sx={{ fontSize: { xs: "1.85rem", md: "2.5rem" }, fontWeight: 700, letterSpacing: "-0.02em", mb: 2 }}>{copy.casesTitle}</Typography>
            <Typography sx={{ color: "#475569", maxWidth: 600, mx: "auto", lineHeight: 1.7 }}>{copy.casesSubtitle}</Typography>
          </Box>
          <Grid container spacing={4}>
            {copy.caseStudies.map((item, i) => (
              <Grid item xs={12} md={4} key={item.title}>
                <motion.div {...fade(i * 0.08)}>
                  <Card sx={{ height: "100%", border: "1px solid #E2E8F0", borderRadius: "14px", boxShadow: "none", transition: "all .25s ease", display: "flex", flexDirection: "column", "&:hover": { borderColor: "#0E7C7B", transform: "translateY(-2px)", boxShadow: "0 16px 32px -20px rgba(15,23,42,0.2)" } }}>
                    <CardContent sx={{ p: 4, pb: 3, flexGrow: 1 }}>
                      <Typography component="h3" sx={{ fontWeight: 700, mb: 3, fontSize: "1.02rem", lineHeight: 1.4, letterSpacing: 0 }}>{item.title}</Typography>
                      {[
                        { key: "problem", label: copy.caseLabels.problem, text: item.problem },
                        { key: "solution", label: copy.caseLabels.solution, text: item.solution },
                      ].map((row) => (
                        <Box key={row.key} sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#475569", mb: 0.5 }}>{row.label}</Typography>
                          <Typography sx={{ color: "#475569", lineHeight: 1.65, fontSize: "0.88rem" }}>{row.text}</Typography>
                        </Box>
                      ))}
                    </CardContent>
                    <Box sx={{ mx: 4, mb: 4, p: 2.25, borderRadius: "10px", backgroundColor: "rgba(14,124,123,0.06)", borderLeft: "3px solid #0E7C7B" }}>
                      <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0E7C7B", mb: 0.5 }}>{copy.caseLabels.result}</Typography>
                      <Typography sx={{ color: "#0F172A", lineHeight: 1.6, fontSize: "0.9rem", fontWeight: 500 }}>{item.result}</Typography>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: { xs: 6, md: 8 }, p: { xs: 3, md: 4 }, borderRadius: "16px", border: "1px solid #E2E8F0", backgroundColor: "#F8FAFC", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.15rem", letterSpacing: "-0.01em", mb: 0.5 }}>{copy.midCtaTitle}</Typography>
              <Typography sx={{ color: "#475569", fontSize: "0.92rem" }}>{copy.closingTrust}</Typography>
            </Box>
            <Button component={Link} to={`${prefix}/book`} sx={{ backgroundColor: "#0F172A", color: "white", fontWeight: 700, borderRadius: "10px", px: 4, py: 1.5, textTransform: "none", "&:hover": { backgroundColor: "#1E293B" } }}>
              {copy.primaryCta}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── FAQ ── */}
      <Box sx={{ py: { xs: 12, md: 16 }, backgroundColor: "#F8FAFC" }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0E7C7B", mb: 2 }}>{copy.faqEyebrow}</Typography>
            <Typography component="h2" sx={{ fontSize: { xs: "1.85rem", md: "2.5rem" }, fontWeight: 700, letterSpacing: "-0.02em", mb: 2 }}>{copy.faqTitle}</Typography>
            <Typography sx={{ color: "#475569" }}>{copy.faqSubtitle}</Typography>
          </Box>
          {copy.faq.map((item) => (
            <Accordion key={item.question} elevation={0} disableGutters sx={{ mb: 1.5, border: "1px solid #E2E8F0", borderRadius: "10px !important", "&:before": { display: "none" }, backgroundColor: "white", overflow: "hidden", transition: "border-color .2s ease", "&:hover": { borderColor: "#CBD5E1" }, "&.Mui-expanded": { borderColor: "#0E7C7B" } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#64748B" }} />} sx={{ py: 1, px: 3, "& .MuiAccordionSummary-content": { my: 1.5 } }}>
                <Typography sx={{ fontWeight: 600, fontSize: "0.98rem", color: "#0F172A" }}>{item.question}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 3, px: 3 }}>
                <Typography sx={{ color: "#475569", lineHeight: 1.7, fontSize: "0.92rem" }}>{item.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ py: { xs: 12, md: 16 }, background: "linear-gradient(180deg, #0B1220 0%, #0F172A 100%)", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <Container maxWidth="sm" sx={{ position: "relative" }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#7BD8D5", mb: 2.5 }}>
            {copy.closingTrust}
          </Typography>
          <Typography component="h2" sx={{ fontSize: { xs: "1.85rem", md: "2.5rem" }, fontWeight: 700, color: "white", letterSpacing: "-0.02em", lineHeight: 1.15, mb: 2.5 }}>
            {copy.closingTitle}
          </Typography>
          <Typography sx={{ color: "#CBD5E1", lineHeight: 1.7, fontSize: "1.02rem", mb: 5, maxWidth: 480, mx: "auto" }}>
            {copy.closingDescription}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 2 }}>
            <Button component={Link} to={`${prefix}/book`} sx={{ backgroundColor: "white", color: "#0F172A", fontWeight: 700, borderRadius: "10px", px: 4, py: 1.5, minHeight: 48, width: { xs: "100%", sm: "auto" }, textTransform: "none", boxShadow: "0 8px 22px -10px rgba(0,0,0,0.5)", "&:hover": { backgroundColor: "#F8FAFC", transform: "translateY(-1px)" } }}>
              {copy.primaryCta}
            </Button>
            <Button component={Link} to={`${prefix}/case`} sx={{ border: "1.5px solid rgba(255,255,255,0.22)", color: "white", fontWeight: 600, borderRadius: "10px", px: 4, py: 1.5, minHeight: 44, width: { xs: "100%", sm: "auto" }, textTransform: "none", "&:hover": { backgroundColor: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.35)" } }}>
              {copy.secondaryCta}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
